import Order from "../components/Order";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import NewOrder from "../components/NewOrder";
import { restaurantOrderService } from "../services";
import { Order as OrderType, OrdersResponse } from "../utils/types";

const AllOrder = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<
    | "new"
    | "confirmed"
    | "preparing"
    | "ready_for_pickup"
    | "out_for_delivery"
    | "delivered"
    | "cancelled"
  >("new");
  const [selectedOrderIndex, setSelectedOrderIndex] = useState<number>(-1);
  const [orders, setOrders] = useState<OrderType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
  });
  const [showDetailsOnMobile, setShowDetailsOnMobile] =
    useState<boolean>(false);

  const statusToTab: Record<string, typeof activeTab> = {
    placed: "new",
    confirmed: "confirmed",
    preparing: "preparing",
    ready_for_pickup: "ready_for_pickup",
    out_for_delivery: "out_for_delivery",
    delivered: "delivered",
    cancelled: "cancelled",
  };

  // Deep-link from notifications: switch to the order's tab
  useEffect(() => {
    const orderId = searchParams.get("orderId");
    if (!orderId) return;

    const openLinkedOrder = async () => {
      try {
        const response: any = await restaurantOrderService.getOrderById(orderId);
        if (response.status && response.data?.orderStatus) {
          const nextTab = statusToTab[response.data.orderStatus] || "new";
          setActiveTab(nextTab);
        }
      } catch (error) {
        console.error("Failed to open linked order:", error);
      }
    };

    openLinkedOrder();
  }, [searchParams]);

  // Fetch orders from API
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const statusMap = {
          new: "placed",
          confirmed: "confirmed",
          preparing: "preparing",
          out_for_delivery: "out_for_delivery",
          delivered: "delivered",
          cancelled: "cancelled",
          ready_for_pickup: "ready_for_pickup",
        };

        const response: OrdersResponse = await restaurantOrderService.getOrders(
          {
            page: pagination.currentPage,
            limit: 20,
            orderStatus: statusMap[activeTab],
          },
        );

        if (response.status && response.data) {
          setOrders(response.data);
          setPagination(response.pagination);
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [activeTab, pagination.currentPage]);

  // Reset selected order when changing tabs unless opening a linked order
  useEffect(() => {
    const orderId = searchParams.get("orderId");
    if (orderId) return;
    setSelectedOrderIndex(-1);
    setShowDetailsOnMobile(false);
  }, [activeTab, searchParams]);

  // Select the linked order once the list has loaded
  useEffect(() => {
    const orderId = searchParams.get("orderId");
    if (!orderId || orders.length === 0) return;
    const index = orders.findIndex((order) => order._id === orderId);
    if (index >= 0) {
      setSelectedOrderIndex(index);
      setShowDetailsOnMobile(true);
    }
  }, [searchParams, orders]);

  // Handle order selection
  const handleOrderClick = (index: number) => {
    setSelectedOrderIndex(index);
    setShowDetailsOnMobile(true);
  };

  // Handle back button on mobile
  const handleBackToList = () => {
    setShowDetailsOnMobile(false);
    setSelectedOrderIndex(-1);
  };

  // Handle order status update
  const handleOrderStatusUpdate = async () => {
    // Refresh orders after status update
    setLoading(true);
    try {
      const statusMap = {
        new: "placed",
        confirmed: "confirmed",
        preparing: "preparing",
        out_for_delivery: "out_for_delivery",
        delivered: "delivered",
        cancelled: "cancelled",
        ready_for_pickup: "ready_for_pickup",
      };

      const response: OrdersResponse = await restaurantOrderService.getOrders(
        {
          page: pagination.currentPage,
          limit: 20,
          orderStatus: statusMap[activeTab],
        },
      );

      if (response.status && response.data) {
        setOrders(response.data);
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error("Error refreshing orders:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-4 md:mt-0 bg-white dark:bg-gray-900 min-h-screen transition-colors">
      <span className="block md:hidden text-[22px] font-semibold px-4 py-2 sticky top-0 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-200 z-10 border-b dark:border-gray-700 transition-colors">
        All Order
      </span>
      <div className="flex bg-[#FFFFFF] dark:bg-gray-900 relative transition-colors">
        {/* Left Section - Order List */}
        <div
          className={`w-full md:w-[45%] border-r dark:border-gray-700 p-2 md:p-6 transition-colors ${showDetailsOnMobile ? "hidden md:block" : "block"}`}
        >
          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            <button
              onClick={() => setActiveTab("new")}
              className={`relative py-3 px-4 text-sm font-medium transition-colors duration-200 flex-shrink-0 whitespace-nowrap
            ${
              activeTab === "new"
                ? "text-[#FF8A00]"
                : "text-gray-500 hover:text-gray-700"
            }`}
            >
              New
              {activeTab === "new" && (
                <span className="absolute left-0 bottom-0 h-[2px] w-full bg-[#FF8A00] rounded-full"></span>
              )}
            </button>

            <button
              onClick={() => setActiveTab("confirmed")}
              className={`relative py-3 px-4 text-sm font-medium transition-colors duration-200 flex-shrink-0 whitespace-nowrap
            ${
              activeTab === "confirmed"
                ? "text-[#FF8A00]"
                : "text-gray-500 hover:text-gray-700"
            }`}
            >
              Confirmed
              {activeTab === "confirmed" && (
                <span className="absolute left-0 bottom-0 h-[2px] w-full bg-[#FF8A00] rounded-full"></span>
              )}
            </button>

            <button
              onClick={() => setActiveTab("preparing")}
              className={`relative py-3 px-4 text-sm font-medium transition-colors duration-200 flex-shrink-0 whitespace-nowrap
            ${
              activeTab === "preparing"
                ? "text-[#FF8A00]"
                : "text-gray-500 hover:text-gray-700"
            }`}
            >
              Preparing
              {activeTab === "preparing" && (
                <span className="absolute left-0 bottom-0 h-[2px] w-full bg-[#FF8A00] rounded-full"></span>
              )}
            </button>

            <button
              onClick={() => setActiveTab("ready_for_pickup")}
              className={`relative py-3 px-4 text-sm font-medium transition-colors duration-200 flex-shrink-0 whitespace-nowrap
            ${
              activeTab === "ready_for_pickup"
                ? "text-[#FF8A00]"
                : "text-gray-500 hover:text-gray-700"
            }`}
            >
              Ready for Pickup
              {activeTab === "ready_for_pickup" && (
                <span className="absolute left-0 bottom-0 h-[2px] w-full bg-[#FF8A00] rounded-full"></span>
              )}
            </button>

            <button
              onClick={() => setActiveTab("out_for_delivery")}
              className={`relative py-3 px-4 text-sm font-medium transition-colors duration-200 flex-shrink-0 whitespace-nowrap
            ${
              activeTab === "out_for_delivery"
                ? "text-[#FF8A00]"
                : "text-gray-500 hover:text-gray-700"
            }`}
            >
              Out For Delivery
              {activeTab === "out_for_delivery" && (
                <span className="absolute left-0 bottom-0 h-[2px] w-full bg-[#FF8A00] rounded-full"></span>
              )}
            </button>

            <button
              onClick={() => setActiveTab("delivered")}
              className={`relative py-3 px-4 text-sm font-medium transition-colors duration-200 flex-shrink-0 whitespace-nowrap
            ${
              activeTab === "delivered"
                ? "text-[#FF8A00]"
                : "text-gray-500 hover:text-gray-700"
            }`}
            >
              Delivered
              {activeTab === "delivered" && (
                <span className="absolute left-0 bottom-0 h-[2px] w-full bg-[#FF8A00] rounded-full"></span>
              )}
            </button>

            <button
              onClick={() => setActiveTab("cancelled")}
              className={`relative py-3 px-4 text-sm font-medium transition-colors duration-200 flex-shrink-0 whitespace-nowrap
            ${
              activeTab === "cancelled"
                ? "text-[#FF8A00]"
                : "text-gray-500 hover:text-gray-700"
            }`}
            >
              Cancelled
              {activeTab === "cancelled" && (
                <span className="absolute left-0 bottom-0 h-[2px] w-full bg-[#FF8A00] rounded-full"></span>
              )}
            </button>
          </div>

          {/* Orders List */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-gray-500">Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-gray-500">No orders found</p>
            </div>
          ) : (
            orders.map((order, index) => (
              <div
                key={order._id}
                onClick={() => handleOrderClick(index)}
                className={`cursor-pointer transition-colors `}
              >
                <Order
                  order={order}
                  selectedOrderIndex={selectedOrderIndex}
                  index={index}
                  tab={activeTab}
                  onStatusUpdate={handleOrderStatusUpdate}
                  onTabChange={(newStatus) => {
                    // Map order status to tab name
                    const tabMap: { [key: string]: typeof activeTab } = {
                      placed: "new",
                      confirmed: "confirmed",
                      preparing: "preparing",
                      ready_for_pickup: "ready_for_pickup",
                      out_for_delivery: "out_for_delivery",
                      delivered: "delivered",
                      cancelled: "cancelled",
                    };
                    const newTab = tabMap[newStatus] || newStatus as typeof activeTab;
                    setActiveTab(newTab);
                  }}
                />
              </div>
            ))
          )}
        </div>

        {/* Right Section - Order Details */}
        <div
          className={`w-full md:w-[55%] border-l ${showDetailsOnMobile ? "block fixed inset-0 z-50 bg-white dark:bg-gray-700 md:static md:z-auto overflow-y-auto h-screen" : "hidden md:block"}`}
        >
          {/* Back Button for Mobile */}
          {showDetailsOnMobile && (
            <div className="md:hidden flex items-center p-4 border-b bg-white sticky top-0 z-10">
              <button
                onClick={handleBackToList}
                className="flex items-center text-gray-700 hover:text-gray-900"
              >
                <svg
                  className="w-6 h-6 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                <span className="font-medium">Back to Orders</span>
              </button>
            </div>
          )}
          <NewOrder
            id={orders[selectedOrderIndex]?._id}
            tab={activeTab}
            selectedOrderIndex={selectedOrderIndex}
          />
        </div>
      </div>
    </div>
  );
};

export default AllOrder;
